import * as Yup from 'yup';

import {
  FIELD_IS_GREATER_THAN_ZERO,
  FIELD_IS_INTEGER,
  FIELD_IS_NUMERIC_STRING,
  FIELD_IS_REQUIRED,
  toFieldMaximumValueErrorMessage,
  toFieldMinimumValueErrorMessage,
} from 'src/common/validation/errors';
import type { MeasureUnitIntFloatValue } from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatField.types';
import type { ICustomerLeadProductSpecificationDataFormValues } from 'src/components/customers/CustomerLeadTypes';
import { toNumber, toNumberOrNull } from 'src/helpers';
import type { ICustomerLeadProduct } from 'src/types/customers/ICustomerLeadProduct';
import type { ICustomerLeadProductSpecificationData } from 'src/types/customers/ICustomerLeadProductSpecificationData';
import {
  type IProductSpecificationFieldReference,
  type IProductSpecificationReference,
  ProductSpecificationFieldType,
  ProductSpecificationRequiredScope,
} from 'src/types/reference/IProductSpecificationReference';
import { SpecificationMeasureUnit } from 'src/types/reference/SpecificationMeasureUnit';

function getBoolSpecificationFieldValidator(field: IProductSpecificationFieldReference) {
  return field.multiselect
    ? Yup.array()
        .nullable()
        .of(Yup.boolean().required(FIELD_IS_REQUIRED))
        .default(field.defaultBool != null ? [field.defaultBool] : [])
    : Yup.boolean()
        .nullable()
        .default(field.defaultBool ?? null);
}

function getEnumSpecificationFieldValidator(field: IProductSpecificationFieldReference) {
  return field.multiselect
    ? Yup.array()
        .nullable()
        .of(
          Yup.string()
            .nullable()
            .default(field.defaultEnumValueId ?? null)
        )
    : Yup.string()
        .nullable()
        .default(field.defaultEnumValueId ?? null);
}

function getTextSpecificationFieldValidator(field: IProductSpecificationFieldReference) {
  return Yup.string()
    .nullable()
    .default(field.defaultTextValue ?? '');
}

function getFloatSpecificationFieldValidator(field: IProductSpecificationFieldReference) {
  const noMeasureUnitsAllowed =
    field.allowedMeasureUnits.length === 1 && field.allowedMeasureUnits[0] === SpecificationMeasureUnit.NONE;

  const numberSchema = Yup.number()
    .typeError(FIELD_IS_NUMERIC_STRING)
    .nullable()
    .when({
      is: () => field.minFloatValue != null,
      then: (schema) =>
        schema.min(
          field.minFloatValue as number,
          toFieldMinimumValueErrorMessage(field.name, field.minFloatValue as number)
        ),
    })
    .when({
      is: () => field.maxFloatValue != null,
      then: (schema) =>
        schema.min(
          field.maxFloatValue as number,
          toFieldMaximumValueErrorMessage(field.name, field.maxFloatValue as number)
        ),
    })
    .default(field.defaultFloatValue ?? null);

  const singleSchema = noMeasureUnitsAllowed
    ? numberSchema
    : Yup.object<MeasureUnitIntFloatValue>({
        measureUnit: Yup.mixed<SpecificationMeasureUnit>()
          .required(FIELD_IS_REQUIRED)
          .default(field.defaultMeasureUnit ?? null),
        value: numberSchema,
      });

  return field.multiselect ? Yup.array().of(singleSchema).default([]) : singleSchema;
}

function getIntSpecificationFieldValidator(field: IProductSpecificationFieldReference) {
  const noMeasureUnitsAllowed =
    field.allowedMeasureUnits.length === 1 && field.allowedMeasureUnits[0] === SpecificationMeasureUnit.NONE;

  const numberSchema = Yup.number()
    .integer(FIELD_IS_INTEGER)
    .typeError(FIELD_IS_NUMERIC_STRING)
    .nullable()
    .when({
      is: () => field.minIntValue != null,
      then: (schema) =>
        schema.min(
          field.minIntValue as number,
          toFieldMinimumValueErrorMessage(field.name, field.minIntValue as number)
        ),
    })
    .when({
      is: () => field.maxIntValue != null,
      then: (schema) =>
        schema.min(
          field.maxIntValue as number,
          toFieldMaximumValueErrorMessage(field.name, field.maxIntValue as number)
        ),
    })
    .default(field.defaultIntValue ?? null);

  const singleSchema = noMeasureUnitsAllowed
    ? numberSchema
    : Yup.object<MeasureUnitIntFloatValue>({
        measureUnit: Yup.mixed<SpecificationMeasureUnit>()
          .required(FIELD_IS_REQUIRED)
          .default(field.defaultMeasureUnit ?? null),
        value: numberSchema,
      });

  return field.multiselect ? Yup.array().of(singleSchema).default([]) : singleSchema;
}

function getDimensionsSpecificationFieldValidator(field: IProductSpecificationFieldReference) {
  const singleSchema = Yup.object({
    measureUnit: Yup.mixed<SpecificationMeasureUnit>().nullable().required(FIELD_IS_REQUIRED).default(null),

    length: Yup.number()
      .nullable()
      .typeError(FIELD_IS_NUMERIC_STRING)
      .moreThan(0, FIELD_IS_GREATER_THAN_ZERO)
      .default(null),
    width: Yup.number()
      .nullable()
      .typeError(FIELD_IS_NUMERIC_STRING)
      .moreThan(0, FIELD_IS_GREATER_THAN_ZERO)
      .default(null),
    height: Yup.number()
      .nullable()
      .when({
        is: () => field.type === ProductSpecificationFieldType.DIM3D,
        then: (schema) =>
          schema.typeError(FIELD_IS_NUMERIC_STRING).moreThan(0, FIELD_IS_GREATER_THAN_ZERO).default(null),
      }),
  }).test('check fields', 'Fill in every field or fill in no one', (_params: unknown, context: Yup.TestContext) => {
    const childFields = context.from?.[0].value;

    if (!childFields) {
      return true;
    }

    const currentValue = {
      length: 'length' in childFields ? childFields.length : null,
      width: 'width' in childFields ? childFields.width : null,

      ...(field.type === ProductSpecificationFieldType.DIM3D
        ? {
            height: 'height' in childFields ? childFields.height : null,
          }
        : {}),
    };

    const fieldsList = Object.values(currentValue);

    const allEmpty = fieldsList.every((value) => !value || String(value).length < 1);
    const allNotEmpty = fieldsList.every((value) => !!value);

    return allEmpty || allNotEmpty;
  });

  return field.multiselect ? Yup.array().of(singleSchema).default([]) : singleSchema;
}

function getIntervalSpecificationFieldValidator(field: IProductSpecificationFieldReference) {
  const singleSchema = Yup.object()
    .shape({
      measureUnit: Yup.mixed<SpecificationMeasureUnit>()
        .required(FIELD_IS_REQUIRED)
        .nullable()
        .default(field.defaultMeasureUnit ?? null),

      minValue: Yup.number()
        .test('minValue', 'Specify at least one value', (minValue, { parent: { maxValue } }) => {
          return minValue != null || maxValue != null;
        })
        .typeError(FIELD_IS_NUMERIC_STRING)
        .nullable()
        .when({
          is: () => field.minIntValue != null,
          then: (schema) =>
            schema.min(
              field.minIntValue as number,
              toFieldMinimumValueErrorMessage(field.name, field.minIntValue as number)
            ),
        })
        .when({
          is: () => field.maxIntValue != null,
          then: (schema) =>
            schema.max(
              field.maxIntValue as number,
              toFieldMaximumValueErrorMessage(field.name, field.maxIntValue as number)
            ),
        })
        .default(null),

      maxValue: Yup.number()
        .typeError(FIELD_IS_NUMERIC_STRING)
        .nullable()
        .when({
          is: () => field.minIntValue != null,
          then: (schema) =>
            schema.min(
              field.minIntValue as number,
              toFieldMinimumValueErrorMessage(field.name, field.minIntValue as number)
            ),
        })
        .when({
          is: () => field.maxIntValue != null,
          then: (schema) =>
            schema.max(
              field.maxIntValue as number,
              toFieldMaximumValueErrorMessage(field.name, field.maxIntValue as number)
            ),
        })
        .default(null),
    })
    .nullable()
    .default(null);

  return field.multiselect ? Yup.array().of(singleSchema.required(FIELD_IS_REQUIRED)).default([]) : singleSchema;
}

function getSpecificationValidator(field: IProductSpecificationFieldReference): Yup.AnySchema {
  const isRequiredField = field.requiredScope !== ProductSpecificationRequiredScope.NONE;

  return getSpecificationFieldValidator(field)
    .when({
      is: () => field.dependsOnId == null && isRequiredField,
      then: (schema) => schema.required(FIELD_IS_REQUIRED),
      otherwise: (schema) => schema.nullable().notRequired(),
    })
    .when({
      is: () =>
        field.dependsOnId == null &&
        isRequiredField &&
        field.type === ProductSpecificationFieldType.ENUM &&
        field.multiselect,
      then: (schema) => (schema as Yup.ArraySchema<unknown[], unknown>).min(1, FIELD_IS_REQUIRED),
    });
}

function getDependencyValidator(field: IProductSpecificationFieldReference, fieldSchema: Yup.AnySchema): Yup.AnySchema {
  if (!field.dependsOnId) {
    return fieldSchema;
  }

  const isRequiredField = field.requiredScope !== ProductSpecificationRequiredScope.NONE;

  return fieldSchema.when(field.dependsOnId, {
    is: (dependsOnIdFieldValue: unknown) => {
      if (!isRequiredField) {
        return false;
      }

      if (typeof dependsOnIdFieldValue === 'boolean') {
        return (
          field.dependsOnBoolValue != null &&
          (field.multiselect
            ? Array.isArray(dependsOnIdFieldValue) && dependsOnIdFieldValue.includes(field.dependsOnBoolValue)
            : field.dependsOnBoolValue === dependsOnIdFieldValue)
        );
      }

      if (typeof dependsOnIdFieldValue === 'string') {
        if (field.dependsOnEnumValueId != null) {
          return field.dependsOnEnumValueId === dependsOnIdFieldValue;
        }

        return !!dependsOnIdFieldValue;
      }

      if (Array.isArray(dependsOnIdFieldValue) && field.dependsOnEnumValueId != null) {
        return dependsOnIdFieldValue.includes(field.dependsOnEnumValueId);
      }

      return false;
    },
    then: (schema) => schema.required(FIELD_IS_REQUIRED),
    otherwise: (schema) => schema.nullable().notRequired(),
  });
}

function getSpecificationFieldValidator(field: IProductSpecificationFieldReference): Yup.AnySchema {
  switch (field.type) {
    case ProductSpecificationFieldType.BOOL:
      return getBoolSpecificationFieldValidator(field);
    case ProductSpecificationFieldType.ENUM:
      return getEnumSpecificationFieldValidator(field);
    case ProductSpecificationFieldType.FLOAT:
      return getFloatSpecificationFieldValidator(field);
    case ProductSpecificationFieldType.INT:
      return getIntSpecificationFieldValidator(field);
    case ProductSpecificationFieldType.TEXT:
      return getTextSpecificationFieldValidator(field);
    case ProductSpecificationFieldType.DIM2D:
    case ProductSpecificationFieldType.DIM3D:
      return getDimensionsSpecificationFieldValidator(field);
    case ProductSpecificationFieldType.INTERVAL:
      return getIntervalSpecificationFieldValidator(field);
    default:
      // ToDo: is it ok? It should not happen in general
      return Yup.string().default(null);
  }
}

function getSpecificationDataValidationSchema(fields: IProductSpecificationFieldReference[]): Yup.ObjectShape {
  return (
    fields.reduce((specificationValidators, field) => {
      const schema = getSpecificationValidator(field);

      specificationValidators[field.id] = getDependencyValidator(field, schema);

      return specificationValidators;
    }, {} as Yup.ObjectShape) || ({} as Yup.ObjectShape)
  );
}

export const formCustomerProductLeadSpecificationEditValidationSchema = (
  fields: IProductSpecificationFieldReference[]
) =>
  Yup.object<ICustomerLeadProductSpecificationDataFormValues>().shape({
    specData: Yup.object<ICustomerLeadProductSpecificationData>(getSpecificationDataValidationSchema(fields)),
  });

function getIntFloatInitialValue(
  field: IProductSpecificationFieldReference,
  specificationValue: ICustomerLeadProductSpecificationData[string]
): ICustomerLeadProductSpecificationData[string] {
  type IntFloatValueType = Record<string, string | number | null>;

  const noMeasureUnitsAllowed =
    field.allowedMeasureUnits.length === 1 && field.allowedMeasureUnits[0] === SpecificationMeasureUnit.NONE;

  if (specificationValue == null) {
    const defaultValue =
      (field.type === ProductSpecificationFieldType.FLOAT ? field.defaultFloatValue : field.defaultIntValue) ?? null;

    const defaultValueWithUnits =
      defaultValue != null
        ? {
            measureUnit: field.defaultMeasureUnit ?? null,
            value: defaultValue,
          }
        : null;

    return field.multiselect ? [] : noMeasureUnitsAllowed ? defaultValue : defaultValueWithUnits;
  }

  if (noMeasureUnitsAllowed) {
    return field.multiselect
      ? Array.isArray(specificationValue)
        ? specificationValue
        : [toNumber(specificationValue as string)]
      : toNumber(specificationValue as string);
  }

  return field.multiselect
    ? Array.isArray(specificationValue)
      ? specificationValue
      : ([specificationValue] as IntFloatValueType[])
    : Array.isArray(specificationValue)
      ? ((specificationValue as IntFloatValueType[])[0] ?? null)
      : specificationValue;
}

function getDimensionsInitialValue(
  field: IProductSpecificationFieldReference,
  specificationValue: ICustomerLeadProductSpecificationData[string]
): ICustomerLeadProductSpecificationData[string] {
  if (specificationValue != null) {
    return field.multiselect
      ? Array.isArray(specificationValue)
        ? specificationValue
        : ([specificationValue] as Record<string, string | number | null>[])
      : specificationValue;
  }

  return field.multiselect ? [] : null;
}

function getIntervalInitialValue(
  field: IProductSpecificationFieldReference,
  specificationValue: ICustomerLeadProductSpecificationData[string]
): ICustomerLeadProductSpecificationData[string] {
  if (specificationValue != null) {
    return field.multiselect
      ? Array.isArray(specificationValue)
        ? specificationValue
        : ([specificationValue] as Record<string, string | number | null>[])
      : specificationValue;
  }

  return field.multiselect ? [] : null;
}

export const toFormCustomerProductLeadSpecificationInitialValues = (
  fields: IProductSpecificationFieldReference[],
  customerLeadProduct?: ICustomerLeadProduct
): ICustomerLeadProductSpecificationDataFormValues => {
  if (!customerLeadProduct) {
    return {
      specData: {},
    };
  }

  const specificationValues = fields.reduce((acc, specField) => {
    const specificationValue = customerLeadProduct.specData[specField.id];

    const specificationFieldFinalValue =
      specField.type === ProductSpecificationFieldType.BOOL
        ? // BOOL type
          specField.multiselect
          ? Array.isArray(specificationValue)
            ? specificationValue.map((item) => !!item)
            : typeof specField.defaultBool === 'boolean'
              ? [specField.defaultBool]
              : []
          : typeof specificationValue === 'boolean'
            ? specificationValue
            : typeof specField.defaultBool === 'boolean'
              ? specField.defaultBool
              : null
        : // INT/FLOAT type
          specField.type === ProductSpecificationFieldType.INT || specField.type === ProductSpecificationFieldType.FLOAT
          ? getIntFloatInitialValue(specField, specificationValue)
          : // DIM2D/DIM3D type
            specField.type === ProductSpecificationFieldType.DIM2D ||
              specField.type === ProductSpecificationFieldType.DIM3D
            ? getDimensionsInitialValue(specField, specificationValue)
            : // INTERVAL type
              specField.type === ProductSpecificationFieldType.INTERVAL
              ? getIntervalInitialValue(specField, specificationValue)
              : // Other types
                (specificationValue ??
                (specField.type === ProductSpecificationFieldType.ENUM
                  ? specField.multiselect
                    ? specField.defaultEnumValueId
                      ? [specField.defaultEnumValueId]
                      : []
                    : specField.defaultEnumValueId
                  : specField.type === ProductSpecificationFieldType.TEXT
                    ? specField.defaultTextValue
                    : null) ??
                null);

    acc[specField.id] = specificationFieldFinalValue;

    return acc;
  }, {} as ICustomerLeadProductSpecificationData);

  return {
    specData: specificationValues,
  };
};

function getIntFloatValue(
  field: IProductSpecificationFieldReference,
  formSpecData: ICustomerLeadProductSpecificationData
): ICustomerLeadProductSpecificationData[string] {
  const intFloatValue = formSpecData[field.id];

  if (intFloatValue == null || intFloatValue === '') {
    return field.multiselect ? [] : null;
  }

  function toIntFloatItemValue(intFloatItem: unknown): ICustomerLeadProductSpecificationData[string] {
    if (intFloatItem == null) {
      return null;
    }

    if (typeof intFloatValue === 'number' || typeof intFloatValue === 'string') {
      return toNumber(intFloatValue);
    }

    if (typeof intFloatItem !== 'object' || !('measureUnit' in intFloatItem) || intFloatItem.measureUnit == null) {
      // Validation should not allow this
      return null;
    }

    return {
      measureUnit: intFloatItem.measureUnit as SpecificationMeasureUnit,

      value: 'value' in intFloatItem ? toNumberOrNull(intFloatItem.value as string | number | null) : null,
    };
  }

  if (Array.isArray(intFloatValue)) {
    const values = intFloatValue.map(toIntFloatItemValue).filter(Boolean);

    return field.multiselect ? (values as ICustomerLeadProductSpecificationData[string]) : (values[0] ?? null);
  }

  const value = toIntFloatItemValue(intFloatValue);

  return field.multiselect ? ([value].filter(Boolean) as ICustomerLeadProductSpecificationData[string]) : value;
}

function getDimensionsValue(
  field: IProductSpecificationFieldReference,
  formSpecData: ICustomerLeadProductSpecificationData
): ICustomerLeadProductSpecificationData[string] {
  type DimensionItemType = Record<string, string | number | null>;

  if (formSpecData[field.id] == null) {
    return field.multiselect ? ([] as DimensionItemType[]) : null;
  }

  function toDimensionsItemValue(dimensionsItem: DimensionItemType): DimensionItemType | null {
    if (
      dimensionsItem.measureUnit == null ||
      dimensionsItem.length == null ||
      dimensionsItem.width == null ||
      (field.type === ProductSpecificationFieldType.DIM3D && dimensionsItem.height == null)
    ) {
      // Validation should not allow this
      return null;
    }

    return {
      measureUnit: dimensionsItem.measureUnit,

      length: toNumber(dimensionsItem.length) || null,
      width: toNumber(dimensionsItem.width) || null,

      ...(field.type === ProductSpecificationFieldType.DIM3D
        ? {
            height: toNumber(dimensionsItem.height as string | number) || null,
          }
        : {}),
    };
  }

  const dimensionsValue = formSpecData[field.id] as DimensionItemType | DimensionItemType[];

  if (Array.isArray(dimensionsValue)) {
    const values = dimensionsValue.map(toDimensionsItemValue).filter(Boolean);

    return field.multiselect ? (values as ICustomerLeadProductSpecificationData[string]) : (values[0] ?? null);
  }

  const value = toDimensionsItemValue(dimensionsValue);

  return field.multiselect ? ([value].filter(Boolean) as ICustomerLeadProductSpecificationData[string]) : value;
}

function getIntervalValue(
  field: IProductSpecificationFieldReference,
  formSpecData: ICustomerLeadProductSpecificationData
) {
  type IntervalItemType = Record<string, string | number | null>;

  if (formSpecData[field.id] == null) {
    return field.multiselect ? ([] as IntervalItemType[]) : null;
  }

  function toIntervalItemValue(dimensionsItem: IntervalItemType): IntervalItemType | null {
    if (dimensionsItem.measureUnit == null || (dimensionsItem.minValue == null && dimensionsItem.maxValue == null)) {
      // Validation should not allow this
      return null;
    }

    return {
      measureUnit: dimensionsItem.measureUnit,

      minValue: toNumberOrNull(dimensionsItem.minValue),
      maxValue: toNumberOrNull(dimensionsItem.maxValue),
    };
  }

  const intervalValue = formSpecData[field.id] as IntervalItemType | IntervalItemType[];

  if (Array.isArray(intervalValue)) {
    const values = intervalValue.map(toIntervalItemValue).filter(Boolean);

    return field.multiselect ? (values as ICustomerLeadProductSpecificationData[string]) : (values[0] ?? null);
  }

  const value = toIntervalItemValue(intervalValue);

  return field.multiselect ? ([value].filter(Boolean) as ICustomerLeadProductSpecificationData[string]) : value;
}

export const toCustomerLeadProductCreateOrUpdateSpecificationData = (
  formSpecData: ICustomerLeadProductSpecificationData,
  specification: IProductSpecificationReference,
  fieldGroup?: string
): Exclude<ICustomerLeadProductSpecificationData, undefined> => {
  return Object.entries(formSpecData).reduce(
    (acc, [currentKey, currentValue]) => {
      const field = specification.fields.find((specField) => specField.id === currentKey) ?? null;

      if (fieldGroup && field && field.group !== fieldGroup) {
        return acc;
      }

      if (
        field &&
        (field.type === ProductSpecificationFieldType.INT || field.type === ProductSpecificationFieldType.FLOAT)
      ) {
        acc[currentKey] = getIntFloatValue(field, formSpecData);

        return acc;
      }

      if (
        field &&
        (field.type === ProductSpecificationFieldType.DIM2D || field.type === ProductSpecificationFieldType.DIM3D)
      ) {
        acc[currentKey] = getDimensionsValue(field, formSpecData);

        return acc;
      }

      if (field && field.type === ProductSpecificationFieldType.INTERVAL) {
        acc[currentKey] = getIntervalValue(field, formSpecData);

        return acc;
      }

      acc[currentKey] =
        currentValue !== undefined && (Array.isArray(currentValue) || String(currentValue).length !== 0)
          ? currentValue
          : null;

      return acc;
    },
    {} as Exclude<ICustomerLeadProductSpecificationData, undefined>
  );
};
